import { gql } from '@apollo/client';

/**
 * One contact's page (card 19): CRM_CONTACT_QUERY_FRONTEND.md §8 (every field),
 * §9 (the linked account and its panel's figures), §19 (notes), §22 (lists), §23
 * (memberships, and the timeline's list and change entries, card 20) and §26
 * (editing, card 21.5: notes' author and `editable`).
 *
 * One root field: the related lists (emails, phones, addresses, tags, lists,
 * notes, account, owner) are batched by the API, not separate requests. The
 * timeline is its own request (CRM_CONTACT_TIMELINE) because it pages.
 */
export const CRM_CONTACT_DETAIL = gql`
  query CrmContactDetail($id: Int!) {
    crmContact(id: $id) {
      id
      userId
      nbSignupId
      source
      prefix
      firstName
      middleName
      lastName
      suffix
      fullName
      primaryEmail
      primaryPhone
      emailOptIn
      emailUnsubAt
      smsOptIn
      doNotCall
      doNotContact
      # Card 20.5: what set each flag, for the banner. Written out twice, not a
      # fragment: the tests run without __typename (ilbp-admin CLAUDE.md).
      doNotCallSet {
        how
        at
        note
        callId
        batchId
        batchSize
        mergedContactId
        admin {
          id
          firstName
          lastName
        }
      }
      doNotContactSet {
        how
        at
        note
        callId
        batchId
        batchSize
        mergedContactId
        admin {
          id
          firstName
          lastName
        }
      }
      emailIsBad
      emailBouncedAt
      nbBannedAt
      canEmail
      canCall
      canText
      countryCode
      region
      city
      lastContactedAt
      lastCalledAt
      callCount
      # Card 21: the open follow-up, over the page.
      followUp {
        id
        occurredAt
        followUpAt
        followUpDue
        outcome
        notes
        admin {
          id
          firstName
          lastName
        }
      }
      supportLevel
      lifecycleStage
      matchMethod
      matchConfidence
      isPossibleDup
      mergedIntoId
      nbCreatedAt
      createdAt
      updatedAt
      joinedAt
      engagement {
        emailsSent
        emailsOpened
        emailsClicked
        emailsBounced
        lastSentAt
        lastOpenedAt
        lastClickedAt
        engagementRate
      }
      emails {
        id
        email
        isPrimary
        isBad
        isInvalid
        bouncedAt
      }
      phones {
        id
        type
        e164
        rawDisplay
        isPrimary
        isBad
      }
      addresses {
        id
        roles
        isPrimary
        address1
        address2
        address3
        city
        state
        zip
        countryCode
      }
      tags {
        tagId
        name
        category
        color
        isSystem
        addedAt
        source
      }
      lists {
        segmentId
        name
        source
        addedAt
      }
      memberships {
        id
        planName
        status
        startedAt
        expiresOn
        source
      }
      notes {
        id
        body
        source
        createdAt
        updatedAt
        # Card 21.5: who wrote it, and whether this admin may change it.
        editable
        admin {
          id
          firstName
          lastName
        }
      }
      account {
        id
        username
        firstName
        lastName
        email
        phone
        level
        totalPoints
        role
        isDeleted
        isBanned
        createdAt
        lastSeenAt
        referralCount
        recommendationCount
      }
      owner {
        id
        firstName
        lastName
      }
    }
  }
`;

/**
 * One page of the merged activity feed, newest first (card 14, §17.7, §19).
 * `counts` (one count per source) is asked on the first page only: it does not
 * change with `kinds` or the cursor.
 */
export const CRM_CONTACT_TIMELINE = gql`
  query CrmContactTimeline($contactId: Int!, $kinds: [CrmTimelineKind!], $take: Int, $after: String, $withCounts: Boolean!) {
    crmContactTimeline(contactId: $contactId, kinds: $kinds, take: $take, after: $after) {
      items {
        id
        kind
        event
        occurredAt
        title
        body
        contactId
        call {
          callId
          outcome
          method
          direction
          durationSec
          followUpAt
          setDoNotCall
          recordingUrl
          admin {
            id
            firstName
            lastName
          }
          author {
            id
            fullName
          }
        }
        sms {
          direction
          toNumber
          failedAt
          errorMessage
        }
        notification {
          status
          reason
        }
        app {
          businessName
          withdrawnAt
          referredUserName
          achievementTitle
          points
        }
        nationBuilder {
          type
          isPrivate
        }
        tag {
          tagId
          name
          color
          source
          admin {
            id
            firstName
            lastName
          }
        }
        list {
          segmentId
          name
          admin {
            id
            firstName
            lastName
          }
        }
        change {
          field
          label
          from
          to
          batchId
          undo
          admin {
            id
            firstName
            lastName
          }
        }
      }
      hasNextPage
      endCursor
      contactIds
      counts @include(if: $withCounts) {
        kind
        count
      }
    }
  }
`;

/**
 * Marks one person do not call or do not contact, or lifts it (card 20.5): a
 * batch update of one (card 20), so it is recorded on their timeline, named
 * in the banner, and can be undone from Batch updates. `expectedCount` is 1:
 * the page is one live record. Lifting needs `note` (how they agreed).
 */
export const CRM_SET_CONTACT_FLAG = gql`
  mutation CrmSetContactFlag($filter: CrmContactFilter!, $change: CrmBatchUpdateInput!) {
    crmRunBatchUpdate(filter: $filter, expectedCount: 1, change: $change) {
      id
      status
      changed
    }
  }
`;
